import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { List, useMediaQuery, useTheme } from '@mui/material';
import { connect, useDispatch, useSelector } from 'react-redux';

import { appToolkit, authToolkit, deviceToolkit, RootState, uiToolkit } from '/store';
import { history } from '/Router/history';
import { IManualTogglingParams } from '/shared/Sidebar';

import { sentryFeedback } from '../../../internals/sentry/initSentryService';

import { INavSidebarProps, INavSidebarStateProps } from './types';
import { getSelectedItems } from './helpers';
import { NavSidebarItem } from './NavSidebarItem';
import { createNavConfig, INavItem, sentryReportNavConfig, statusMonitorNavConfig } from './navConfig';
import { SidebarStyled, VersionSection } from './styles';

const NavSidebarComponent: FC<INavSidebarProps> = memo((props) => {
  const { role, appName, location, onOpeningStatusChange } = props;

  const access = useSelector(appToolkit.selectors.accessScope);
  const isSidebarPermanentlyOpen = useSelector(uiToolkit.selectors.navSidebarOpen);
  const sidebarSide = useSelector(uiToolkit.selectors.navSidebarSide);
  const device = useSelector(deviceToolkit.selectors.devices);
  const isSentryAvailable = process.env.SENTRY_ENABLE === 'true';

  const routerPath = useRef('');
  const [selectedItems, setSelectedItems] = useState(() => new Set<string>([]));
  const [isSidebarOpen, setIsSidebarOpen] = useState(isSidebarPermanentlyOpen);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const dispatch = useDispatch();
  const toggleNavBarPermanentlyOpen = useCallback(
    (isOpen: boolean) => {
      dispatch(uiToolkit.actions.toggleNavSidebar(isOpen));
    },
    [dispatch]
  );

  const getSelectedItemsByPath = useCallback(
    (items: INavItem[]) => {
      const itemsFullList: INavItem[] = [...items, statusMonitorNavConfig];

      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (location?.pathname && itemsFullList) {
        const foundItems = getSelectedItems(itemsFullList, location.pathname) || [];
        setSelectedItems(new Set(foundItems));
        routerPath.current = location.pathname;
      }
    },
    [location?.pathname]
  );

  const onSelectItems = (captions: string[]) => setSelectedItems(new Set(captions));

  const handleRouterRedirect = (route: string) => {
    routerPath.current = route;
    history.push(route);
  };

  const list: INavItem[] = useMemo(() => {
    const newList = createNavConfig({
      appName,
      role,
      access,
      hidden: device[0] === undefined,
    });
    getSelectedItemsByPath(newList);
    return newList;
  }, [appName, role, access, device, getSelectedItemsByPath]);

  const sidebarManualTogglingParams = useMemo<IManualTogglingParams>(
    () => ({
      isOpenedManually: isSidebarPermanentlyOpen,
      openManually: () => toggleNavBarPermanentlyOpen(true),
      closeManually: () => toggleNavBarPermanentlyOpen(false),
    }),
    [isSidebarPermanentlyOpen, toggleNavBarPermanentlyOpen]
  );

  const handleSidebarTogglingStatusChange = useCallback((isOpen: boolean) => {
    setIsSidebarOpen(isOpen);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (location && routerPath && location.pathname !== routerPath.current) {
      getSelectedItemsByPath(list);
    }
  }, [getSelectedItemsByPath, list, location, location?.pathname]);

  useEffect(() => {
    onOpeningStatusChange(isSidebarOpen);
  }, [isSidebarOpen, onOpeningStatusChange]);

  const handleSentryReport = async () => {
    const form = await sentryFeedback.createForm();
    form.appendToDom();
    form.open();
  };
  return (
    <SidebarStyled
      side={sidebarSide}
      manualTogglingParams={sidebarManualTogglingParams}
      onTogglingStatusChange={handleSidebarTogglingStatusChange}
      shouldBeOpenOnHover={true}
      variant={matches ? 'temporary' : 'permanent'}
      $isSidebarOpen={isSidebarOpen}
    >
      <List component='nav'>
        {list.map((item) => (
          <NavSidebarItem
            key={item.caption}
            item={item}
            openSidebar={isSidebarOpen}
            selectedItems={selectedItems}
            onSelectItem={onSelectItems}
            onRouterRedirect={handleRouterRedirect}
          />
        ))}
      </List>
      <VersionSection>
        {isSentryAvailable && (
          <NavSidebarItem
            key={sentryReportNavConfig.caption}
            item={sentryReportNavConfig}
            openSidebar={isSidebarOpen}
            onButtonClick={handleSentryReport}
            isButton
          />
        )}
        <NavSidebarItem
          key={statusMonitorNavConfig.caption}
          item={statusMonitorNavConfig}
          openSidebar={isSidebarOpen}
          selectedItems={selectedItems}
          onSelectItem={onSelectItems}
          onRouterRedirect={handleRouterRedirect}
        />
        <span>{isSidebarOpen && process.env.NODE_ENV === 'production' && `v${__CI_BUILD__}`}</span>
      </VersionSection>
    </SidebarStyled>
  );
});

const mapStateToProps = (state: RootState): INavSidebarStateProps => ({
  role: authToolkit.selectors.role(state),
  appName: appToolkit.selectors.appRouteName(state),
});

export const NavSidebar = connect(mapStateToProps)(NavSidebarComponent);
